import { AlertCircle, CheckCircle2, XCircle } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { EntryReceiptRequirement } from '../../../models/Entry';
import { NON_COMPLIANCE_REQUIREMENT_CODES } from '../../models/Receive';

interface RequirementsListProps {
    requirements: EntryReceiptRequirement[];
    isLoading?: boolean;
    onDelete?: (requirementCode: string) => void;
}

export const RequirementsList: React.FC<RequirementsListProps> = ({
    requirements,
    isLoading = false,
    onDelete,
}) => {
    const { t } = useTranslation('merchandiseV3');

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                    <p className="text-sm text-muted-foreground">
                        {t('receive.requirements.loading', 'Cargando requisitos...')}
                    </p>
                </div>
            </div>
        );
    }

    if (requirements.length === 0) {
        return (
            <div className="bg-background border border-border rounded-lg p-4">
                <div className="text-center py-8">
                    <p className="text-sm text-muted-foreground">
                        {t('receive.requirements.empty', 'No hay requisitos registrados')}
                    </p>
                </div>
            </div>
        );
    }

    const nonComplianceRequirements = requirements.filter(req =>
        NON_COMPLIANCE_REQUIREMENT_CODES.includes(req.requirement_code as any)
    );
    const otherRequirements = requirements.filter(req =>
        !NON_COMPLIANCE_REQUIREMENT_CODES.includes(req.requirement_code as any)
    );

    return (
        <div className="space-y-4">
            <h3 className="text-lg font-semibold text-foreground">
                {t('receive.requirements.title', 'Requisitos de Calidad')}
            </h3>

            {/* Requisitos de incumplimiento */}
            {nonComplianceRequirements.length > 0 && (
                <div className="space-y-2">
                    <h4 className="text-sm font-medium text-red-600 dark:text-red-400">
                        {t('receive.requirements.non_compliance', 'Incumplimientos')}
                    </h4>
                    {nonComplianceRequirements.map((requirement) => (
                        <div
                            key={requirement.id}
                            className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg p-3"
                        >
                            <div className="flex items-start justify-between gap-3">
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                        <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400" />
                                        <span className="font-medium text-red-900 dark:text-red-100">
                                            {requirement.requirement_code}
                                        </span>
                                    </div>
                                    {requirement.notes && (
                                        <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                                            {requirement.notes}
                                        </p>
                                    )}
                                    {requirement.attachments && requirement.attachments.length > 0 && (
                                        <div className="mt-2">
                                            <p className="text-xs text-red-600 dark:text-red-400">
                                                {t('receive.requirements.attachments', 'Evidencias')}: {requirement.attachments.length}
                                            </p>
                                        </div>
                                    )}
                                </div>
                                {onDelete && (
                                    <button
                                        onClick={() => onDelete(requirement.requirement_code)}
                                        className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200"
                                    >
                                        <XCircle className="w-4 h-4" />
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Otros requisitos */}
            {otherRequirements.length > 0 && (
                <div className="space-y-2">
                    <h4 className="text-sm font-medium text-foreground">
                        {t('receive.requirements.other', 'Otros Requisitos')}
                    </h4>
                    {otherRequirements.map((requirement) => (
                        <div
                            key={requirement.id}
                            className="bg-background border border-border rounded-lg p-3"
                        >
                            <div className="flex items-start justify-between gap-3">
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                        <CheckCircle2 className="w-4 h-4 text-green-600 dark:text-green-400" />
                                        <span className="font-medium text-foreground">
                                            {requirement.requirement_code}
                                        </span>
                                    </div>
                                    {requirement.notes && (
                                        <p className="text-sm text-muted-foreground mt-1">
                                            {requirement.notes}
                                        </p>
                                    )}
                                    {requirement.attachments && requirement.attachments.length > 0 && (
                                        <div className="mt-2">
                                            <p className="text-xs text-muted-foreground">
                                                {t('receive.requirements.attachments', 'Evidencias')}: {requirement.attachments.length}
                                            </p>
                                        </div>
                                    )}
                                </div>
                                {onDelete && (
                                    <button
                                        onClick={() => onDelete(requirement.requirement_code)}
                                        className="text-muted-foreground hover:text-destructive"
                                    >
                                        <XCircle className="w-4 h-4" />
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

